import React, { Component } from 'react';

/**
 * ErrorBoundary catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree
 * that crashed. It prevents the entire app from unmounting.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // You could also log the error to an error reporting service
    console.error('ErrorBoundary caught an error', error, info);
  }

  render() {
    if (this.state.hasError) {
      // Premium styled fallback UI with glassmorphism and subtle animation
      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-r from-primary-600 to-secondary-600 p-4">
          <div className="max-w-md rounded-xl bg-white bg-opacity-80 backdrop-blur-lg shadow-xl p-8 text-center">
            <h2 className="mb-4 text-2xl font-bold text-gray-800">Something went wrong.</h2>
            <p className="mb-4 text-gray-600">We’re sorry for the inconvenience. Please try refreshing the page or contact support.</p>
            {this.state.error && (
              <details className="mb-4 text-left">
                <summary className="text-xs text-red-600 font-semibold cursor-pointer">Technical Details</summary>
                <pre className="mt-2 p-2 bg-red-50 text-red-700 text-[11px] rounded overflow-auto max-h-32 font-mono whitespace-pre-wrap">
                  {this.state.error.message || String(this.state.error)}
                </pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 transition"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
